// src/lib/opportunity/orgFormGeneratedClient.ts

import { opportunityFetch } from "./opportunityFetch.js";
import { requireSupabase } from "@/lib/supabase";
import type { OrgFormGeneratedOutput } from "./orgFormInstanceTypes.js";

const SIGN_PATH = "/api/opportunity/form-generated-sign";
const COMMIT_PATH = "/api/opportunity/form-generated-commit";

type SignOk = {
  ok: true;
  bucket: string;
  path: string;
  token: string;
  signed_url: string;
  output_id: string;
  revision_label: string;
};

type CommitOk = {
  ok: true;
  output: OrgFormGeneratedOutput;
};

function isSignOk(x: unknown): x is SignOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    o.ok === true &&
    typeof o.bucket === "string" &&
    typeof o.path === "string" &&
    typeof o.token === "string" &&
    typeof o.output_id === "string"
  );
}

function isCommitOk(x: unknown): x is CommitOk {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return o.ok === true && typeof o.output === "object" && o.output !== null;
}

export type UploadFormPdfResult =
  | { ok: true; output: OrgFormGeneratedOutput }
  | { ok: false; error: string };

export async function uploadGeneratedOrgFormPdf(args: {
  formInstanceId: string;
  blob: Blob;
}): Promise<UploadFormPdfResult> {
  const sb = requireSupabase();
  const byteSize = args.blob.size;
  if (byteSize <= 0) {
    return { ok: false, error: "Generated PDF is empty — nothing to upload." };
  }

  const sign = await opportunityFetch<unknown>(SIGN_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      form_instance_id: args.formInstanceId,
      content_type: "application/pdf",
      byte_size: byteSize,
    }),
  });
  if (!sign.ok) return { ok: false, error: sign.error };
  if (!isSignOk(sign.data)) {
    const err = (sign.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Sign response invalid" };
  }
  const s = sign.data;

  const { error: upErr } = await sb.storage
    .from(s.bucket)
    .uploadToSignedUrl(s.path, s.token, args.blob, {
      contentType: "application/pdf",
      upsert: false,
    });
  if (upErr) return { ok: false, error: upErr.message };

  const commit = await opportunityFetch<unknown>(COMMIT_PATH, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      form_instance_id: args.formInstanceId,
      output_id: s.output_id,
      object_path: s.path,
      revision_label: s.revision_label,
      byte_size: byteSize,
    }),
  });
  if (!commit.ok) return { ok: false, error: commit.error };
  if (!isCommitOk(commit.data)) {
    const err = (commit.data as { error?: unknown })?.error;
    return { ok: false, error: typeof err === "string" ? err : "Commit response invalid" };
  }
  return { ok: true, output: commit.data.output };
}
